/* global interfaceConfig */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getConferenceState } from '../../../../base/conference/functions';
import { translate } from '../../../../base/i18n';
import { browser } from '../../../../base/lib-meet-hour';
import { resolveAppPackage } from '../../../../base/util';
import { isDevelopment } from '../../../../base/util/devUtils';
import { NOTIFICATION_TIMEOUT, showNotification as showGlobalNotification } from '../../../../notifications';
import { DONATION_MAREQUE } from '../../../actionTypes';
import {
    bindConFeeOrderItem,
    bindDonationOrderItems,
    bindDonationStream,
    bindIsDonationDone,
    bindPaymentOrderNo,
    bindShowHideForm,
    setPopupOverlay
} from '../../../actions';
import { safeConferenceSendMessage } from '../../../conferenceUtils';
import {
    buildAmazonIntentPayload,
    buildDonationStreamData,
    buildOAuthReturnUrl,
    buildPayPalCreateOrderPayload,
    buildPayPalRecurringPayload
} from '../../../walletPayments';
import { getLoaderStyle, paymentMethod } from '../styles';
import {
    postAmazonPaymentIntent,
    postPayPalCreateOrder,
    postPayPalRecurringToken,
    postStripePaymentIntent
} from '../utility/apiActions';
import { getGatewayConfig } from '../utility/constants';
import { processAmazonPayGateway, processPayPalGateway, processStripeGateway } from '../utility/gatewayProcessing';
import { PAGES } from '../utility/pages';


/**
 * Payment Method for Multiple Payment Options in clickandpledge
 */
function PaymentMethod({ setShowCreditCard, showCreditCard, onSubmit, onNavigate, getCampaignRaisedData, requireContactInfo, selectedChargeDate, t }) {
    const [ isPayPalLoading, setIsPayPalLoading ] = useState(false);
    const [ isAmazonPayLoading, setIsAmazonPayLoading ] = useState(false);
    const [ isStripeLoading, setIsStripeLoading ] = useState(false);
    const [ stripeInstance, setStripeInstance ] = useState(null);
    const [ paymentRequest, setPaymentRequest ] = useState(null);
    const [ canMakePayment, setCanMakePayment ] = useState(null);
    const [ amountError, setAmountError ] = useState(false);
    const [ notification, setNotification ] = useState(null);
    const popupMonitorRef = useRef(null);
    const popupWindowRef = useRef(null);
    const popupMessageHandledRef = useRef(false);
    const isPaymentProcessingRef = useRef(false);
    const paypalCreateOrderInFlightRef = useRef(false);
    const paypalRecurringTokenInFlightRef = useRef(false);
    const stripeContactRef = useRef(null);
    const stripePayingWithRef = useRef(null);

    // Refs to track active window message listeners for cleanup
    const amazonPayMessageHandlerRef = useRef(null);
    const paypalOneTimeMessageHandlerRef = useRef(null);
    const paypalRecurringMessageHandlerRef = useRef(null);

    const isAnyPaymentLoading = isPayPalLoading || isAmazonPayLoading || isStripeLoading;

    const dispatch = useDispatch();

    // Get data from Redux store
    const cnpPayQuicklyData = useSelector(state => state['features/clickandpledge']?.cnpPayQuicklyData || {});
    const finalDonationAmt = useSelector(state => state['features/clickandpledge']?.finalDonationAmt || '');
    const meetData = useSelector(state => state['features/clickandpledge']?.meetData || {});
    const meetDataRef = useRef(meetData);

    useEffect(() => {
        meetDataRef.current = meetData;
    }, [ meetData ]);
    const recurringSelection = useSelector(state => state['features/clickandpledge']?.recurringSelection || 'One Time Only');
    const donationStream = useSelector(state => state['features/clickandpledge']?.donationStream || []);
    const installements = useSelector(state => state['features/clickandpledge']?.installementSelected || 999);
    const installementSelected = typeof installements === 'number' ? installements : 999;
    const isAnonymous = useSelector(state => state['features/clickandpledge']?.isAnonymous || false);
    const publicComments = useSelector(state => state['features/clickandpledge']?.publicComments || '');
    const { locationURL } = useSelector(state => state['features/base/connection']);
    const donationAmountSelected = useSelector(
        state => state['features/clickandpledge']?.donationAmountSelected || ''
    );
    const formSettings = useSelector(state => state['features/clickandpledge']?.formSettings || {});
    const chargeDateData = useSelector(state => state['features/clickandpledge']?.chargeDateData || {});
    const renderPopupOverlay = useSelector(state => state['features/clickandpledge']?.renderPopupOverlay);

    // Keep refs in sync with latest props & state to avoid stale closures in event listeners
    const selectedChargeDateRef = useRef(selectedChargeDate);
    const chargeDateDataRef = useRef(chargeDateData);
    const cnpPayQuicklyDataRef = useRef(cnpPayQuicklyData);
    const finalDonationAmtRef = useRef(finalDonationAmt);
    const donationAmountSelectedRef = useRef(donationAmountSelected);
    const recurringSelectionRef = useRef(recurringSelection);
    const installementSelectedRef = useRef(installementSelected);
    const formSettingsRef = useRef(formSettings);
    const donationStreamRef = useRef(donationStream);
    const isAnonymousRef = useRef(isAnonymous);
    const publicCommentsRef = useRef(publicComments);
    const conferenceRef = useRef(null);
    const getCampaignRaisedDataRef = useRef(getCampaignRaisedData);
    const onNavigateRef = useRef(onNavigate);
    const onSubmitRef = useRef(onSubmit);
    const handlePaymentMethodRef = useRef(null);
    const handleCancelRef = useRef(null);

    useEffect(() => {
        selectedChargeDateRef.current = selectedChargeDate;
    }, [ selectedChargeDate ]);

    useEffect(() => {
        chargeDateDataRef.current = chargeDateData;
    }, [ chargeDateData ]);

    useEffect(() => {
        cnpPayQuicklyDataRef.current = cnpPayQuicklyData;
    }, [ cnpPayQuicklyData ]);

    useEffect(() => {
        finalDonationAmtRef.current = finalDonationAmt;
    }, [ finalDonationAmt ]);

    useEffect(() => {
        donationAmountSelectedRef.current = donationAmountSelected;
    }, [ donationAmountSelected ]);

    useEffect(() => {
        recurringSelectionRef.current = recurringSelection;
    }, [ recurringSelection ]);

    useEffect(() => {
        installementSelectedRef.current = installementSelected;
    }, [ installementSelected ]);

    useEffect(() => {
        formSettingsRef.current = formSettings;
    }, [ formSettings ]);

    useEffect(() => {
        donationStreamRef.current = donationStream;
    }, [ donationStream ]);

    useEffect(() => {
        isAnonymousRef.current = isAnonymous;
    }, [ isAnonymous ]);

    useEffect(() => {
        publicCommentsRef.current = publicComments;
    }, [ publicComments ]);

    useEffect(() => {
        getCampaignRaisedDataRef.current = getCampaignRaisedData;
    }, [ getCampaignRaisedData ]);

    useEffect(() => {
        onNavigateRef.current = onNavigate;
    }, [ onNavigate ]);

    useEffect(() => {
        onSubmitRef.current = onSubmit;
    }, [ onSubmit ]);

    /**
     * Get the most up-to-date donation amount from refs or state.
     * Checks finalDonationAmt first, then donationAmountSelected.
     *
     * @returns {number} The current donation amount as a float (e.g. 50.0).
     */
    function getCurrentDonationAmount() {
        const amt = finalDonationAmtRef.current
            || finalDonationAmt
            || donationAmountSelectedRef.current
            || donationAmountSelected
            || 0;

        return parseFloat(amt) || 0;
    }

    // Get conference for sending messages
    const conference = useSelector(state => getConferenceState(state)?.conference);

    useEffect(() => {
        conferenceRef.current = conference;
    }, [ conference ]);

    // Determines current App Mode
    const isDevelopmentApi = isDevelopment(); // meethour.org environment
    const accountMode = cnpPayQuicklyData?.forms?.Mode || 'P';
    const cnpAppMode = isDevelopmentApi ? 'BZ' : accountMode === 'Y' ? 'TEST' : 'LIVE'; // LIVE means meethour.io environment
    const gatewayConfig = getGatewayConfig(cnpAppMode);

    // Load Stripe script on mount
    useEffect(() => {
        loadStripeScript();
    }, [ cnpPayQuicklyData ]);

    // Keep Stripe PaymentRequest amount in sync whenever user updates donation amount
    useEffect(() => {
        if (!paymentRequest || !stripeInstance) {
            return;
        }

        const currentAmount = getCurrentDonationAmount();
        const totalAmount = Math.round(currentAmount * 100);

        if (totalAmount > 0) {
            try {
                paymentRequest.update({
                    total: {
                        label: 'Total Charge',
                        amount: totalAmount
                    }
                });
            } catch {
                // Ignore if payment sheet is active or not allowed to update
            }
        }
    }, [ paymentRequest, stripeInstance, finalDonationAmt, donationAmountSelected ]);

    // Cleanup popup monitor and any active message listeners on unmount
    useEffect(() => () => {
        isPaymentProcessingRef.current = false;
        if (popupMonitorRef.current) {
            window.clearInterval(popupMonitorRef.current);
            popupMonitorRef.current = null;
        }

        // Remove any lingering window message listeners to prevent memory leaks
        if (amazonPayMessageHandlerRef.current) {
            window.removeEventListener('message', amazonPayMessageHandlerRef.current);
            amazonPayMessageHandlerRef.current = null;
        }
        if (paypalOneTimeMessageHandlerRef.current) {
            window.removeEventListener('message', paypalOneTimeMessageHandlerRef.current);
            paypalOneTimeMessageHandlerRef.current = null;
        }
        if (paypalRecurringMessageHandlerRef.current) {
            window.removeEventListener('message', paypalRecurringMessageHandlerRef.current);
            paypalRecurringMessageHandlerRef.current = null;
        }
    }, []);

    // Stop loaders if popup overlay is dismissed via cancel
    useEffect(() => {
        if (!renderPopupOverlay?.isVisible) {
            stopPopupMonitor();
            isPaymentProcessingRef.current = false;
            setIsAmazonPayLoading(false);
            setIsPayPalLoading(false);
            setIsStripeLoading(false);
        }
    }, [ renderPopupOverlay?.isVisible ]);

    // Clear red border error when a valid donation amount is selected
    useEffect(() => {
        if (isDonationAmountValid() && amountError) {
            setAmountError(false);
            const amountButtons = document.querySelectorAll('.amountButtonSettings');

            amountButtons.forEach(btn => {
                btn.style.removeProperty('border');
                btn.style.removeProperty('outline');
            });
        }
    }, [ donationAmountSelected, finalDonationAmt ]);

    /**
     * Check if the donation amount is valid (not empty and greater than 0)
     */
    function isDonationAmountValid() {
        return getCurrentDonationAmount() > 0;
    }

    /**
     * Format selected charge date into Click&Pledge YY/MM/DD format if it is in the future.
     *
     * @param {string|null} dateStr - Selected charge date string.
     * @returns {string} Formatted charge date or empty string.
     */
    function getFormattedChargeDate(dateStr) {
        if (!dateStr || !chargeDateDataRef.current?.AllowPaymentScheduling) {
            return '';
        }

        const ndate = new Date(dateStr);
        const today = new Date();

        today.setHours(0, 0, 0, 0);

        if (ndate > today) {
            const yy = String(ndate.getFullYear()).slice(-2);
            const mm = String(ndate.getMonth() + 1).padStart(2, '0');
            const dd = String(ndate.getDate()).padStart(2, '0');

            return `${yy}/${mm}/${dd}`;
        }

        return '';
    }


    /**
     * Normalize error payloads into a short, user-friendly string.
     *
     * @param {*} error - Error or payload from payment APIs.
     * @param {string} fallback - Fallback message when no error details exist.
     * @returns {string}
     */
    function toShortMessage(error, fallback) {
        if (!error) {
            return fallback;
        }

        const raw = typeof error === 'string'
            ? error
            : error.message || error.error || error.resultData || fallback;
        const text = String(raw).trim();

        if (!text) {
            return fallback;
        }

        return text.length > 120 ? `${text.slice(0, 117)}...` : text;
    }

    /**
     * Show an inline notification at the top of the payment section.
     *
     * @param {'success'|'error'} type - Notification type.
     * @param {string} title - Notification title (bold).
     * @param {string} message - Message to display (regular weight).
     * @returns {void}
     */
    function showNotification(notification_type, title, message) {
        setNotification({
            type: notification_type,
            title,
            message
        });
        window.clearTimeout(window.cnpNotificationTimer);
        window.cnpNotificationTimer = window.setTimeout(() => {
            setNotification(null);
        }, 10000);

        try {
            dispatch(showGlobalNotification({
                appearance: notification_type === 'error' ? 'error' : 'success',
                title,
                description: message
            }, NOTIFICATION_TIMEOUT));
        } catch {
            // Ignore if notifications feature flag disabled
        }
    }

    /**
     * Translate keys and show as an inline notification with title and message.
     *
     * @param {'success'|'error'} type - Notification type.
     * @param {string} titleKey - i18n key for title from main.json.
     * @param {string} messageKey - i18n key for message from main.json.
     * @param {Object} args - Interpolation arguments for i18n.
     * @returns {void}
     */
    function showTranslatedNotification(type, titleKey, messageKey, args = {}) {
        const title = t(titleKey);
        const message = t(messageKey, args);

        showNotification(type, title, message);
    }

    /**
     * Highlight amount buttons with red border to indicate missing amount
     */
    function showAmountError() {
        setAmountError(true);
        const amountButtons = document.querySelectorAll('.amountButtonSettings');

        amountButtons.forEach(btn => {
            btn.style.setProperty('border', '1.5px solid white', 'important');
            btn.style.setProperty('outline', '1.5px solid red', 'important');
        });

        const wrapper = document.querySelector('.amount_btns_wrapper');

        if (wrapper) {
            wrapper.animate([
                { transform: 'translateX(0)' },
                { transform: 'translateX(-5px)' },
                { transform: 'translateX(5px)' },
                { transform: 'translateX(-5px)' },
                { transform: 'translateX(5px)' },
                { transform: 'translateX(0)' }
            ], {
                duration: 400,
                easing: 'ease-in-out'
            });
        }
    }

    /**
     * Build OAuth return URL and include desktop app deep-link metadata for Electron.
     *
     * @returns {string}
     */
    function getOAuthReturnUrl() {
        const isElectronApp = typeof browser !== 'undefined'
            && typeof browser.isElectron === 'function'
            && browser.isElectron();

        return buildOAuthReturnUrl({
            appPackage: isElectronApp
                ? resolveAppPackage(typeof interfaceConfig === 'object' ? interfaceConfig : undefined, 'desktop')
                : undefined,
            includeAppDeepLink: isElectronApp,
            origin: locationURL.origin,
            platform: isElectronApp ? 'desktop' : undefined
        });
    }

    /**
     * Stop the active popup monitor and clear references.
     *
     * @returns {void}
     */
    function stopPopupMonitor() {
        if (popupMonitorRef.current) {
            window.clearInterval(popupMonitorRef.current);
            popupMonitorRef.current = null;
        }
        popupWindowRef.current = null;
    }

    /**
     * Start monitoring a popup window for manual close events.
     *
     * @param {Window|null} nextPopupWindow - Popup window reference.
     * @returns {void}
     */
    function startPopupMonitor(nextPopupWindow) {
        stopPopupMonitor();
        popupWindowRef.current = nextPopupWindow;

        popupMonitorRef.current = window.setInterval(() => {
            const currentWindow = popupWindowRef.current;

            if (!currentWindow || currentWindow.closed) {
                stopPopupMonitor();

                if (popupMessageHandledRef.current) {
                    return;
                }

                // Close overlay and stop loaders when popup closes
                dispatch(setPopupOverlay(false, null));
                isPaymentProcessingRef.current = false;
                setIsAmazonPayLoading(false);
                setIsPayPalLoading(false);
                setIsStripeLoading(false);
            }
        }, 500);
    }

    /**
     * Load Stripe.js script
     */
    function loadStripeScript() {
        if (window.Stripe) {
            initializeStripe();

            return;
        }

        // window.Stripe alone is not a sufficient guard: a second call made while the first script is still
        // downloading does not see it yet and appends another tag, which is exactly what Stripe warns about
        // ('Stripe.js was loaded more than one time'). Wait for the tag that is already there instead.
        const pendingScript = document.querySelector('script[src^="https://js.stripe.com/v3"]');

        if (pendingScript) {
            pendingScript.addEventListener('load', initializeStripe, { once: true });

            return;
        }

        const script = document.createElement('script');

        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        script.onload = () => {
            initializeStripe();
        };
        document.body.appendChild(script);
    }

    /**
     * Initialize Stripe instance and Payment Request
     */
    function initializeStripe() {
        if (!window.Stripe) {
            return;
        }

        const stripePK = gatewayConfig.STRIPE_PK;
        const stripeFactory = window.Stripe;
        const stripe = stripeFactory(stripePK, { apiVersion: '2020-08-27' });

        setStripeInstance(stripe);

        const currentAmount = getCurrentDonationAmount();
        const totalAmount = Math.round(currentAmount * 100);
        const pr = stripe.paymentRequest({
            country: 'US',
            currency: 'usd',
            total: {
                label: 'Total Charge',
                amount: totalAmount > 0 ? totalAmount : 100
            },
            requestPayerName: true,
            requestPayerEmail: true
        });

        // Check availability
        pr.canMakePayment().then(result => {
            if (result) {
                setCanMakePayment({
                    applePay: result.applePay,
                    googlePay: result.googlePay,
                    link: result.link
                });
                setPaymentRequest(pr);
            } else {
                setCanMakePayment(null);
            }
        });
    }

    /**
     * Create Stripe Payment Intent (Helper)
     */
    async function createStripePaymentIntent() {
        const cnpData = cnpPayQuicklyDataRef.current || cnpPayQuicklyData;
        const accId = cnpData?.forms?.OrganizationID || '';
        const someKey = cnpData?.forms?.Key || '';
        const currentAmount = getCurrentDonationAmount();
        const totalAmount = Math.round(currentAmount * 100);

        const stripeIntentURL = gatewayConfig.STRIPE_INTENT_API;

        try {
            const tokenInt = await dispatch(postStripePaymentIntent({
                url: stripeIntentURL,
                payload: {
                    organizationId: accId,
                    amount: totalAmount,
                    randomStr: someKey
                }
            }));

            if (!tokenInt || tokenInt instanceof Error || tokenInt.error) {
                const errorMsg = tokenInt instanceof Error
                    ? tokenInt.message
                    : tokenInt?.error || '';

                showTranslatedNotification(
                    'error',
                    'clickandpledge.StripePaymentIntentFailedTitle',
                    'clickandpledge.StripePaymentIntentFailed',
                    { error: toShortMessage(errorMsg, 'Failed to initialize payment') }
                );

                return null;
            }

            return tokenInt;
        } catch (error) {
            showTranslatedNotification(
                'error',
                'clickandpledge.StripePaymentIntentFailedTitle',
                'clickandpledge.StripePaymentIntentFailed',
                { error: toShortMessage(error, '') }
            );

            return null;
        }
    }

    const handlePaymentMethod = async ev => {
        // Switch to spinner state now that user has approved in the sheet
        dispatch(setPopupOverlay(true, null));
        const contact = stripeContactRef.current;
        const payingWith = stripePayingWithRef.current || 'stripe';

        try {
            // Validate payment method exists
            if (!ev.paymentMethod?.id) {
                ev.complete('fail');
                showTranslatedNotification('error', 'clickandpledge.StripePaymentMethodInvalidTitle', 'clickandpledge.StripePaymentMethodInvalid');

                return;
            }

            // Create Payment Intent
            const clientSecret = await createStripePaymentIntent();

            if (!clientSecret) {
                ev.complete('fail');
                showTranslatedNotification('error', 'clickandpledge.StripePaymentStartFailedTitle', 'clickandpledge.StripePaymentStartFailed');

                return;
            }

            // Confirm Payment
            const confirmResult = await stripeInstance.confirmCardPayment(clientSecret, {
                payment_method: ev.paymentMethod.id
            }, { handleActions: false });

            if (confirmResult.error) {
                showTranslatedNotification(
                    'error',
                    'clickandpledge.StripeConfirmationFailedTitle',
                    'clickandpledge.StripeConfirmationFailed',
                    { error: toShortMessage(confirmResult.error, '') }
                );
                ev.complete('fail');
            } else {
                ev.complete('success');

                if (confirmResult.paymentIntent.status === 'requires_action') {
                    // Handle 3DS or other actions if needed
                    const result = await stripeInstance.confirmCardPayment(clientSecret);

                    if (result.error) {
                        showTranslatedNotification(
                            'error',
                            'clickandpledge.StripeAuthenticationFailedTitle',
                            'clickandpledge.StripeAuthenticationFailed',
                            { error: toShortMessage(result.error, '') }
                        );
                    } else {
                        // Success after action
                        await processStripeSuccess(result.paymentIntent.client_secret, payingWith, contact);
                    }
                } else {
                    // Success immediately
                    await processStripeSuccess(clientSecret, payingWith, contact);
                }
            }
        } finally {
            isPaymentProcessingRef.current = false;
            setIsStripeLoading(false);
            dispatch(setPopupOverlay(false, null));
        }
    };

    const handleCancel = () => {
        isPaymentProcessingRef.current = false;
        setIsStripeLoading(false);
        dispatch(setPopupOverlay(false, null));
    };

    // Keep handlers in refs so Stripe event listeners registered once always invoke fresh logic
    handlePaymentMethodRef.current = handlePaymentMethod;
    handleCancelRef.current = handleCancel;

    // Register Stripe PaymentRequest event listeners once when instance & request are available
    useEffect(() => {
        if (!paymentRequest || !stripeInstance) {
            return;
        }

        const onPaymentMethod = ev => {
            if (handlePaymentMethodRef.current) {
                return handlePaymentMethodRef.current(ev);
            }
        };

        const onCancel = () => {
            if (handleCancelRef.current) {
                return handleCancelRef.current();
            }
        };

        paymentRequest.on('paymentmethod', onPaymentMethod);
        paymentRequest.on('cancel', onCancel);

        // Without this every re-run of the effect added another pair, and a payment method event was then handled
        // once per pair.
        return () => {
            paymentRequest.off?.('paymentmethod', onPaymentMethod);
            paymentRequest.off?.('cancel', onCancel);
        };
    }, [ paymentRequest, stripeInstance ]);

    /**
     * Handle Stripe Payment Click (Apple Pay / Google Pay)
     * @param {string} payingWith - 'applePay' or 'gPay'
     */
    function handleStripePaymentClick(payingWith) {
        if (!stripeInstance || !paymentRequest) {
            showTranslatedNotification('error', 'clickandpledge.StripeNotReadyTitle', 'clickandpledge.StripeNotReady');
            isPaymentProcessingRef.current = false;
            setIsStripeLoading(false);

            return;
        }

        // Prevent update while any payment is already open/processing
        if (isPaymentProcessingRef.current || isAnyPaymentLoading) {
            return;
        }

        // Validate contact info before proceeding
        requireContactInfo(contact => {
            _processStripePayment(payingWith, contact);
        });
    }

    /**
     * Internal: processes Stripe payment sheet after contact info is confirmed.
     *
     * @param {string} payingWith - 'applePay' or 'gPay'
     * @param {{ fname: string, lname: string, email: string }} contact - Verified contact details.
     */
    function _processStripePayment(payingWith, contact) {
        if (!stripeInstance || !paymentRequest) {
            isPaymentProcessingRef.current = false;
            setIsStripeLoading(false);

            return;
        }

        isPaymentProcessingRef.current = true;
        setIsStripeLoading(true);
        stripeContactRef.current = contact;
        stripePayingWithRef.current = payingWith;

        // Update payment request amount and show sheet
        const currentAmount = getCurrentDonationAmount();
        const totalAmount = Math.round(currentAmount * 100);

        try {
            paymentRequest.update({
                total: {
                    label: 'Total Charge',
                    amount: totalAmount
                }
            });

            // Show payment sheet with dummy window to trigger "Complete Payment" message in overlay
            dispatch(setPopupOverlay(true, {
                closed: false,
                focus: () => {
                    // Stripe sheet focuses itself
                },
                close: () => {
                    // Browser payment sheet cannot be closed programmatically
                }
            }));
            paymentRequest.show();
        } catch {
            // Payment sheet is already open or failed to show — reset state
            isPaymentProcessingRef.current = false;
            setIsStripeLoading(false);
            dispatch(setPopupOverlay(false, null));
        }
    }

    /**
     * Process Stripe Gateway Result and Navigate
     * @param {*} paymentIntent - The Stripe Payment Intent client secret or ID
     * @param {*} payingWith - The method used (e.g., 'applePay' or 'gPay') for logging or specific handling if needed
     * @param {*} contactOverride - Contact details { fname, lname, email }
     */
    async function processStripeSuccess(paymentIntent, payingWith, contactOverride = null) {
        try {
            const currentMeetData = meetDataRef.current || meetData || {};
            const currentAmount = getCurrentDonationAmount();
            const result = await processStripeGateway({
                paymentIntent, // This is the client secret or intent ID
                cnpPayQuicklyData: cnpPayQuicklyDataRef.current || cnpPayQuicklyData,
                formSettings: formSettingsRef.current || formSettings,
                meetData: currentMeetData,
                finalDonationAmt: currentAmount,
                recurringType: recurringSelectionRef.current || recurringSelection,
                installments: installementSelectedRef.current || installementSelected,
                contactOverride,
                chargeDate: getFormattedChargeDate(selectedChargeDateRef.current)
            });

            handleGatewayResult(result, payingWith, contactOverride);
        } catch (error) {
            showTranslatedNotification(
                'error',
                'clickandpledge.PaymentFailedDescriptionTitle',
                'clickandpledge.PaymentFailedDescription',
                { error: toShortMessage(error, '') }
            );
        }
    }

    /**
     * Handle Amazon Pay Click
     */
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    async function handleAmazonPayClick() {
        if (isPaymentProcessingRef.current || isAnyPaymentLoading) {
            return;
        }

        if (!isDonationAmountValid()) {
            showAmountError();

            return;
        }

        try {
            // Validate contact info — if missing, dialog is shown and we abort this attempt
            const hasContact = requireContactInfo(contact => {
                // Re-invoke with the filled-in contact details
                _processAmazonPay(contact);
            });

            if (!hasContact) {
                // Contact info is missing; user will be prompted to fill it in
            }

        } catch (error) {
            showTranslatedNotification(
                'error',
                'clickandpledge.AmazonPayFailedTitle',
                'clickandpledge.AmazonPayFailed',
                { error: toShortMessage(error, '') }
            );
            isPaymentProcessingRef.current = false;
            setIsAmazonPayLoading(false);
        }
    }

    /**
     * Internal: processes Amazon Pay after contact info is confirmed.
     *
     * @param {{ fname: string, lname: string, email: string }} contact - Verified contact details.
     */
    async function _processAmazonPay(contact) {
        isPaymentProcessingRef.current = true;
        setIsAmazonPayLoading(true);

        try {
            const accId = cnpPayQuicklyData?.forms?.OrganizationID || '';
            const someKey = cnpPayQuicklyData?.forms?.Key || '';
            const ipAddress = cnpPayQuicklyData?.forms?.IPAddress || '';
            const { fname, lname, email } = contact;

            // Get total amount - convert to cents (integer)
            const currentAmount = getCurrentDonationAmount();
            const totalAmount = Math.round(currentAmount * 100);

            if (!accId || !someKey) {
                showTranslatedNotification('error', 'clickandpledge.AmazonPayCredentialsMissingTitle', 'clickandpledge.AmazonPayCredentialsMissing');
                isPaymentProcessingRef.current = false;
                setIsAmazonPayLoading(false);

                return;
            }

            if (totalAmount <= 0) {
                showTranslatedNotification('error', 'clickandpledge.AmazonPayInvalidAmountTitle', 'clickandpledge.AmazonPayInvalidAmount');
                isPaymentProcessingRef.current = false;
                setIsAmazonPayLoading(false);

                return;
            }

            // Determine return URL
            const returnUrl = getOAuthReturnUrl();

            // Determine API URL based on environment
            const azApiURL = gatewayConfig.AMAZON_PAY_API;

            // Build intent object
            const intentPayload = buildAmazonIntentPayload({
                accountId: accId,
                amount: totalAmount,
                contact: {
                    fname,
                    lname,
                    email
                },
                ipAddress,
                randomStr: someKey,
                returnUrl,
                userAgent: navigator.userAgent
            });

            const amazonPayIntent = await dispatch(postAmazonPaymentIntent({
                url: azApiURL,
                payload: intentPayload
            }));

            let approvalUrl = null;
            let intentError = null;

            if (!amazonPayIntent) {
                intentError = t('clickandpledge.AmazonPayIntentFailed');
            } else if (amazonPayIntent instanceof Error) {
                intentError = amazonPayIntent.message || t('clickandpledge.AmazonPayIntentFailed');
            } else if (amazonPayIntent.error) {
                intentError = typeof amazonPayIntent.error === 'string'
                    ? amazonPayIntent.error
                    : amazonPayIntent.error.message || JSON.stringify(amazonPayIntent.error);
            } else if (amazonPayIntent.message && !amazonPayIntent.URL && !amazonPayIntent.url) {
                intentError = amazonPayIntent.message;
            } else if (typeof amazonPayIntent === 'string') {
                approvalUrl = amazonPayIntent;
            } else if (typeof amazonPayIntent === 'object') {
                approvalUrl = amazonPayIntent.URL || amazonPayIntent.url || amazonPayIntent.approvalUrl || amazonPayIntent.redirect_url;
            }

            if (intentError || !approvalUrl) {
                showTranslatedNotification(
                    'error',
                    'clickandpledge.AmazonPayIntentFailedTitle',
                    'clickandpledge.AmazonPayIntentFailed',
                    { error: toShortMessage(intentError, t('clickandpledge.AmazonPayMissingPaymentUrl') || 'Failed to obtain Amazon Pay approval URL') }
                );
                isPaymentProcessingRef.current = false;
                setIsAmazonPayLoading(false);

                return;
            }

            // Open popup
            const width = 600;
            const height = 600;
            const left = (screen.width - width) / 2;
            const top = (screen.height - height) / 2;

            const amazonWindow = window.open(
                approvalUrl,
                'AmazonPayWindow',
                `width=${width},height=${height},top=${top},left=${left}`
            );

            if (!amazonWindow) {
                showTranslatedNotification('error', 'clickandpledge.errors.PopupBlockedTitle', 'clickandpledge.errors.PopupBlocked');
                isPaymentProcessingRef.current = false;
                setIsAmazonPayLoading(false);

                return;
            }

            // Reset message handled flag before registering new listener.
            popupMessageHandledRef.current = false;
            if (amazonPayMessageHandlerRef.current) {
                window.removeEventListener('message', amazonPayMessageHandlerRef.current);
                amazonPayMessageHandlerRef.current = null;
            }

            // Show popup overlay
            dispatch(setPopupOverlay(true, amazonWindow));

            // Start monitoring popup
            startPopupMonitor(amazonWindow);

            // Message handler for Amazon Pay completion
            const handleAmazonPayMessage = async event => {
                // Verify origin
                if (event.origin !== window.location.origin) {
                    return;
                }

                const isSuccess = event.data?.type === 'AMAZON_PAY_SUCCESS'
                    || (Boolean(event.data?.az_payment_intent) && event.data?.az_payment_intent !== 'failed');
                const isFailure = event.data?.type === 'AMAZON_PAY_CANCEL'
                    || event.data?.type === 'AMAZON_PAY_FAILURE'
                    || event.data?.az_payment_intent === 'failed';

                if (isSuccess) {
                    const az_payment_intent = event.data.az_payment_intent;

                    if (!az_payment_intent) {
                        showTranslatedNotification('error', 'clickandpledge.AmazonPayFailedTitle', 'clickandpledge.AmazonPayFailed', { error: 'No payment intent received' });
                        isPaymentProcessingRef.current = false;
                        setIsAmazonPayLoading(false);
                        dispatch(setPopupOverlay(false, null));

                        return;
                    }

                    // Remove listener and clear ref
                    window.removeEventListener('message', handleAmazonPayMessage);
                    amazonPayMessageHandlerRef.current = null;
                    popupMessageHandledRef.current = true;
                    stopPopupMonitor();

                    // Clear popup window reference to show loader, but keep overlay visible
                    dispatch(setPopupOverlay(true, null));

                    try {
                        const currentMeetData = meetDataRef.current || meetData || {};

                        // Process the payment through the gateway
                        const gatewayResult = await processAmazonPayGateway({
                            paymentIntent: az_payment_intent,
                            cnpPayQuicklyData: cnpPayQuicklyDataRef.current || cnpPayQuicklyData,
                            formSettings: formSettingsRef.current || formSettings,
                            meetData: currentMeetData,
                            finalDonationAmt: getCurrentDonationAmount(),
                            recurringType: recurringSelectionRef.current || recurringSelection,
                            installments: installementSelectedRef.current || installementSelected,
                            contactOverride: {
                                fname,
                                lname,
                                email
                            },
                            chargeDate: getFormattedChargeDate(selectedChargeDateRef.current)
                        });

                        handleGatewayResult(gatewayResult, 'amazonPay', {
                            fname,
                            lname,
                            email
                        });
                    } catch (error) {
                        showTranslatedNotification(
                            'error',
                            'clickandpledge.AmazonPayFailedTitle',
                            'clickandpledge.AmazonPayFailed',
                            {
                                error: toShortMessage(error, '')
                            }
                        );
                        if (onSubmitRef.current || onSubmit) {
                            const submitHandler = onSubmitRef.current || onSubmit;

                            submitHandler('amazonPay', {
                                success: false,
                                error: toShortMessage(error, '')
                            });
                        }
                    } finally {
                        isPaymentProcessingRef.current = false;
                        setIsAmazonPayLoading(false);
                        dispatch(setPopupOverlay(false, null));
                    }
                } else if (isFailure) {
                    window.removeEventListener('message', handleAmazonPayMessage);
                    amazonPayMessageHandlerRef.current = null;
                    popupMessageHandledRef.current = true;
                    stopPopupMonitor();
                    isPaymentProcessingRef.current = false;
                    setIsAmazonPayLoading(false);
                    dispatch(setPopupOverlay(false, null));
                    showTranslatedNotification(
                        'error',
                        'clickandpledge.AmazonPayFailedOrCancelledTitle',
                        'clickandpledge.AmazonPayFailedOrCancelled'
                    );
                    if (onSubmitRef.current || onSubmit) {
                        const submitHandler = onSubmitRef.current || onSubmit;

                        submitHandler('amazonPay', {
                            success: false,
                            error: 'Payment failed or was cancelled'
                        });
                    }
                }
            };

            amazonPayMessageHandlerRef.current = handleAmazonPayMessage;
            window.addEventListener('message', handleAmazonPayMessage);

        } catch (error) {
            showTranslatedNotification(
                'error',
                'clickandpledge.AmazonPayFailedTitle',
                'clickandpledge.AmazonPayFailed',
                { error: toShortMessage(error, '') }
            );
            isPaymentProcessingRef.current = false;
            setIsAmazonPayLoading(false);
            dispatch(setPopupOverlay(false, null));
        }
    }

    /**
     * Handle PayPal One-Time Payment Click
     * Opens PayPal popup, captures payment intent from oauth.html, and processes payment
     */
    function handlePayPalOneTimeClick() {
        if (isPaymentProcessingRef.current || isAnyPaymentLoading) {
            return;
        }

        // Validate contact info before proceeding
        requireContactInfo(contact => {
            _processPayPalOneTime(contact);
        });
    }

    /**
     * Internal: processes PayPal One-Time after contact info is confirmed.
     *
     * @param {{ fname: string, lname: string, email: string }} contact - Verified contact details.
     */
    async function _processPayPalOneTime(contact) {
        isPaymentProcessingRef.current = true;
        setIsPayPalLoading(true);

        try {
            // Validate donation amount
            if (!isDonationAmountValid()) {
                showAmountError();
                isPaymentProcessingRef.current = false;
                setIsPayPalLoading(false);

                return;
            }

            const accId = cnpPayQuicklyData?.forms?.OrganizationID || '';
            const someKey = cnpPayQuicklyData?.forms?.Key || '';
            const currentAmount = getCurrentDonationAmount();
            const totalAmount = currentAmount.toFixed(2);
            const currencyCode = cnpPayQuicklyData?.forms?.FormSettings?.CurrencyCode?.toUpperCase() || 'USD';
            const itemName = formSettings?.itemName || 'Donation Amount';
            const itemSKU = formSettings?.sku || 'DONATION';

            if (!accId || !someKey) {
                showTranslatedNotification('error', 'clickandpledge.errors.PayPalCredentialsMissingTitle', 'clickandpledge.errors.PayPalCredentialsMissing');
                isPaymentProcessingRef.current = false;
                setIsPayPalLoading(false);

                return;
            }

            if (currentAmount <= 0) {
                showTranslatedNotification('error', 'clickandpledge.PayPalInvalidAmountTitle', 'clickandpledge.PayPalInvalidAmount');
                isPaymentProcessingRef.current = false;
                setIsPayPalLoading(false);

                return;
            }

            // Build order data for PayPal API
            const returnUrl = getOAuthReturnUrl();

            // Call PayPal Create Order API through Redux dispatch (backend proxy)
            // Prevent duplicate API calls from React StrictMode or race conditions
            if (paypalCreateOrderInFlightRef.current) {
                return;
            }

            paypalCreateOrderInFlightRef.current = true;

            const apiUrl = gatewayConfig.PAYPAL_CREATE_ORDER_API;
            const payload = buildPayPalCreateOrderPayload({
                amount: totalAmount,
                currencyCode,
                itemName,
                itemSKU,
                orgId: accId,
                randomStr: someKey,
                returnUrl
            });

            let responseData;

            try {
                // Use Redux dispatch to call backend (proxies request to avoid CORS)
                responseData = await dispatch(postPayPalCreateOrder({
                    url: apiUrl,
                    payload
                }));
            } finally {
                paypalCreateOrderInFlightRef.current = false;
            }

            // Validate response data exists
            if (!responseData || responseData instanceof Error) {
                const errorMsg = responseData instanceof Error ? responseData.message : 'PayPal response is empty or undefined';

                throw new Error(errorMsg);
            }

            if (responseData.error || responseData.message) {
                const errorMsg = responseData.error || responseData.message;

                throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
            }

            const orderId = responseData.id;
            const approvalLink = responseData.links?.find(link => link.rel === 'approve')?.href;

            if (!orderId || !approvalLink) {
                throw new Error('PayPal response missing order ID or approval link');
            }

            // Open PayPal popup
            const width = 600;
            const height = 600;
            const left = (screen.width - width) / 2;
            const top = (screen.height - height) / 2;

            const paypalWindow = window.open(
                approvalLink,
                'PayPalOneTimeWindow',
                `width=${width},height=${height},top=${top},left=${left}`
            );

            if (!paypalWindow) {
                showTranslatedNotification('error', 'clickandpledge.errors.PopupBlockedTitle', 'clickandpledge.errors.PopupBlocked');
                isPaymentProcessingRef.current = false;
                setIsPayPalLoading(false);

                return;
            }

            // Store approval link for reference
            window.paypalOneTimeOrderId = orderId;

            dispatch(setPopupOverlay(true, paypalWindow));
            startPopupMonitor(paypalWindow);

            // Reset message handled flag before registering new listener.
            // Remove any previously registered PayPal one-time listener to avoid stacking.
            popupMessageHandledRef.current = false;
            if (paypalOneTimeMessageHandlerRef.current) {
                window.removeEventListener('message', paypalOneTimeMessageHandlerRef.current);
                paypalOneTimeMessageHandlerRef.current = null;
            }

            // Listen for postMessage from oauth.html
            const handlePayPalOneTimeMessage = async function handlePayPalOneTimeMessage(event) {
                // Security: reject messages from untrusted origins
                if (event.origin !== locationURL.origin) {
                    return;
                }

                if (popupMessageHandledRef.current) {
                    return;
                }

                // Check if this is a PayPal-related message from oauth.html
                const { token, ba_token } = event.data || {};

                if (token || ba_token) {

                    // Remove the event listener and clear the ref
                    window.removeEventListener('message', handlePayPalOneTimeMessage);
                    paypalOneTimeMessageHandlerRef.current = null;
                    popupMessageHandledRef.current = true;
                    stopPopupMonitor();

                    // Clear popup window reference to show loader, but keep overlay visible
                    dispatch(setPopupOverlay(true, null));

                    try {
                        // Use the order ID we created earlier
                        const paymentOrderId = window.paypalOneTimeOrderId || orderId;
                        const currentMeetData = meetDataRef.current || meetData || {};

                        // Process the payment through the gateway
                        const result = await processPayPalGateway({
                            orderID: paymentOrderId,
                            cnpPayQuicklyData: cnpPayQuicklyDataRef.current || cnpPayQuicklyData,
                            formSettings: formSettingsRef.current || formSettings,
                            meetData: currentMeetData,
                            finalDonationAmt: getCurrentDonationAmount(),
                            recurringType: 'One Time Only',
                            installments: 1,
                            contactOverride: contact,
                            chargeDate: getFormattedChargeDate(selectedChargeDateRef.current)
                        });

                        // Handle gateway result
                        handleGatewayResult(result, 'paypalOneTime', contact);
                    } catch (error) {
                        showTranslatedNotification(
                            'error',
                            'clickandpledge.PayPalProcessingFailedTitle',
                            'clickandpledge.PayPalProcessingFailed',
                            { error: toShortMessage(error, 'Payment processing failed') }
                        );
                    } finally {
                        isPaymentProcessingRef.current = false;
                        setIsPayPalLoading(false);
                        dispatch(setPopupOverlay(false, null));
                    }
                }
            };

            paypalOneTimeMessageHandlerRef.current = handlePayPalOneTimeMessage;
            window.addEventListener('message', handlePayPalOneTimeMessage);

        } catch (error) {
            showTranslatedNotification(
                'error',
                'clickandpledge.PayPalOrderCreateFailedTitle',
                'clickandpledge.PayPalOrderCreateFailed',
                { error: toShortMessage(error, 'Failed to create PayPal order') }
            );
            isPaymentProcessingRef.current = false;
            setIsPayPalLoading(false);
            dispatch(setPopupOverlay(false, null));
        }
    }

    /**
     * Handle PayPal Recurring Click
     */
    function handlePayPalRecurringClick() {
        if (isPaymentProcessingRef.current || isAnyPaymentLoading) {
            return;
        }

        if (!isDonationAmountValid()) {
            showAmountError();

            return;
        }

        try {
            // Validate contact info - when it is missing requireContactInfo shows the
            // dialog and does not run the callback, so this attempt simply stops here.
            requireContactInfo(contact => {
                _processPayPalRecurring(contact);
            });
        } catch (error) {
            showTranslatedNotification(
                'error',
                'clickandpledge.PayPalRecurringFailedTitle',
                'clickandpledge.PayPalRecurringFailed',
                { error: toShortMessage(error, '') }
            );
            isPaymentProcessingRef.current = false;
            setIsPayPalLoading(false);
        }
    }

    /**
     * Internal: processes PayPal Recurring after contact info is confirmed.
     *
     * @param {{ fname: string, lname: string, email: string }} contact - Verified contact details.
     */
    async function _processPayPalRecurring(contact) {
        isPaymentProcessingRef.current = true;
        setIsPayPalLoading(true);

        try {
            const accId = cnpPayQuicklyData?.forms?.OrganizationID || '';
            const someKey = cnpPayQuicklyData?.forms?.Key || '';
            const currentAmount = getCurrentDonationAmount();

            if (!accId || !someKey) {
                showTranslatedNotification('error', 'clickandpledge.errors.PayPalCredentialsMissingTitle', 'clickandpledge.errors.PayPalCredentialsMissing');
                isPaymentProcessingRef.current = false;
                setIsPayPalLoading(false);

                return;
            }

            if (currentAmount <= 0) {
                showTranslatedNotification('error', 'clickandpledge.PayPalInvalidAmountTitle', 'clickandpledge.PayPalInvalidAmount');
                isPaymentProcessingRef.current = false;
                setIsPayPalLoading(false);

                return;
            }

            const apiUrl = gatewayConfig.PAYPAL_RECURRING_API;

            // Prevent duplicate API calls from React StrictMode or race conditions
            if (paypalRecurringTokenInFlightRef.current) {
                return;
            }

            paypalRecurringTokenInFlightRef.current = true;

            const returnUrl = getOAuthReturnUrl();

            const payload = buildPayPalRecurringPayload({
                orgId: accId,
                randomStr: someKey,
                returnUrl
            });

            let tokenData;

            try {
                tokenData = await dispatch(postPayPalRecurringToken({
                    url: apiUrl,
                    payload
                }));
            } finally {
                paypalRecurringTokenInFlightRef.current = false;
            }

            let approvalLink = null;
            let recurringError = null;

            if (!tokenData) {
                recurringError = 'No approval link received';
            } else if (tokenData instanceof Error) {
                recurringError = tokenData.message || 'PayPal recurring request failed';
            } else if (tokenData.error) {
                recurringError = typeof tokenData.error === 'string'
                    ? tokenData.error
                    : tokenData.error.message || JSON.stringify(tokenData.error);
            } else if (tokenData.message && !tokenData.approvalUrl && !tokenData.URL) {
                recurringError = tokenData.message;
            } else if (typeof tokenData === 'string') {
                approvalLink = tokenData;
            } else if (typeof tokenData === 'object') {
                approvalLink = tokenData.approvalUrl || tokenData.URL || tokenData.url || tokenData.links?.find(link => link.rel === 'approve')?.href;
            }

            if (recurringError || !approvalLink) {
                showTranslatedNotification(
                    'error',
                    'clickandpledge.PayPalRecurringFailedTitle',
                    'clickandpledge.PayPalRecurringFailed',
                    { error: toShortMessage(recurringError, 'No approval link received') }
                );
                isPaymentProcessingRef.current = false;
                setIsPayPalLoading(false);

                return;
            }

            // Open popup
            const width = 600;
            const height = 600;
            const left = (screen.width - width) / 2;
            const top = (screen.height - height) / 2;

            const paypalWindow = window.open(
                approvalLink, // This is the approval link
                'PayPalRecurrWindow',
                `width=${width},height=${height},top=${top},left=${left}`
            );

            if (!paypalWindow) {
                showTranslatedNotification('error', 'clickandpledge.errors.PopupBlockedTitle', 'clickandpledge.errors.PopupBlocked');
                isPaymentProcessingRef.current = false;
                setIsPayPalLoading(false);

                return;
            }

            // Hidden inputs accessed via refs (azPayIntentRef, paypalTokenRef, paypalBaTokenRef)

            dispatch(setPopupOverlay(true, paypalWindow));
            startPopupMonitor(paypalWindow);

            // Reset message handled flag before registering new listener.
            // Remove any previously registered PayPal recurring listener to avoid stacking.
            popupMessageHandledRef.current = false;
            if (paypalRecurringMessageHandlerRef.current) {
                window.removeEventListener('message', paypalRecurringMessageHandlerRef.current);
                paypalRecurringMessageHandlerRef.current = null;
            }

            // Message handler for PayPal Recurring completion
            const handlePayPalRecurringMessage = async event => {
                // Security: reject messages from untrusted origins
                if (event.origin !== locationURL.origin) {
                    return;
                }

                if (popupMessageHandledRef.current) {
                    return;
                }

                // Check if this is a PayPal-related message from oauth.html
                const { token, ba_token } = event.data || {};
                const paypalToken = token || ba_token;

                if (paypalToken) {

                    // Remove the event listener and clear the ref
                    window.removeEventListener('message', handlePayPalRecurringMessage);
                    paypalRecurringMessageHandlerRef.current = null;
                    popupMessageHandledRef.current = true;
                    stopPopupMonitor();

                    // Clear popup window reference to show loader, but keep overlay visible
                    dispatch(setPopupOverlay(true, null));

                    if (!paypalToken) {
                        showTranslatedNotification('error', 'clickandpledge.PayPalRecurringFailedTitle', 'clickandpledge.PayPalRecurringFailed', { error: 'No token received from PayPal' });
                        isPaymentProcessingRef.current = false;
                        setIsPayPalLoading(false);
                        dispatch(setPopupOverlay(false, null));

                        return;
                    }

                    try {
                        const currentMeetData = meetDataRef.current || meetData || {};

                        // Process the payment through the gateway
                        const result = await processPayPalGateway({
                            orderID: paypalToken,
                            cnpPayQuicklyData: cnpPayQuicklyDataRef.current || cnpPayQuicklyData,
                            formSettings: formSettingsRef.current || formSettings,
                            meetData: currentMeetData,
                            finalDonationAmt: getCurrentDonationAmount(),
                            recurringType: recurringSelectionRef.current || recurringSelection,
                            installments: installementSelectedRef.current || installementSelected,
                            contactOverride: contact,
                            chargeDate: getFormattedChargeDate(selectedChargeDateRef.current)
                        });

                        // If payment was successful, handle success flow
                        handleGatewayResult(result, 'paypalRecurr', contact);
                    } catch (error) {
                        showTranslatedNotification(
                            'error',
                            'clickandpledge.PayPalRecurringFailedTitle',
                            'clickandpledge.PayPalRecurringFailed',
                            { error: toShortMessage(error, '') }
                        );
                    } finally {
                        isPaymentProcessingRef.current = false;
                        setIsPayPalLoading(false);
                        dispatch(setPopupOverlay(false, null));
                    }
                }
            };

            paypalRecurringMessageHandlerRef.current = handlePayPalRecurringMessage;
            window.addEventListener('message', handlePayPalRecurringMessage);

        } catch (error) {
            showTranslatedNotification(
                'error',
                'clickandpledge.PayPalRecurringFailedTitle',
                'clickandpledge.PayPalRecurringFailed',
                { error: toShortMessage(error, '') }
            );
            isPaymentProcessingRef.current = false;
            setIsPayPalLoading(false);
            dispatch(setPopupOverlay(false, null));
        }
    }

    /**
     * Handle Gateway Result and Navigation
     */
    async function handleGatewayResult(result, method, contact = null) {
        if (result.success) {
            showTranslatedNotification('success', 'clickandpledge.PaymentSuccessTitle', 'clickandpledge.PaymentSuccess');
            const currentMeetData = meetDataRef.current || meetData || {};
            const firstName = contact?.fname || contact?.first_name || currentMeetData?.first_name || '';
            const lastName = contact?.lname || contact?.last_name || currentMeetData?.last_name || '';
            const email = contact?.email || currentMeetData?.email || '';

            const udata = {
                display_name: `${firstName} ${lastName}`.trim(),
                first_name: firstName,
                last_name: lastName,
                email
            };

            const currentAmount = getCurrentDonationAmount();
            const currentComments = publicCommentsRef.current ?? publicComments;
            const currentAnonymous = isAnonymousRef.current ?? isAnonymous;
            const currentPeriodicity = recurringSelectionRef.current || recurringSelection;
            const currentDonationStream = donationStreamRef.current || donationStream;
            const currentConf = conferenceRef.current || conference;
            const currentRaisedFunc = getCampaignRaisedDataRef.current || getCampaignRaisedData;
            const currentNavigate = onNavigateRef.current || onNavigate;

            const newStreamData = buildDonationStreamData({
                amount: currentAmount > 0 ? currentAmount.toString() : '0',
                comments: currentComments,
                firstName: udata.first_name,
                gatewayTransactionNumber: result.gatewayTransactionNumber,
                isAnonymous: currentAnonymous,
                lastName: udata.last_name,
                periodicity: currentPeriodicity,
                transactionNumber: result.transactionNumber
            });

            const updatedStreamData = [ ...currentDonationStream, newStreamData ];

            dispatch(bindDonationStream(updatedStreamData));

            // Refresh campaign raised data to update the goal meter
            if (currentRaisedFunc) {
                await currentRaisedFunc();
            }

            const isBroadcastSent = safeConferenceSendMessage(currentConf, {
                type: DONATION_MAREQUE,
                streamData: newStreamData
            }, 'clickandpledge-givent:PaymentMethod:walletMarquee');

            if (!isBroadcastSent) {
                dispatch(showGlobalNotification({
                    titleKey: 'connection.DISCONNECTED',
                    descriptionKey: 'clickandpledge.broadcastSkippedDisconnected'
                }, NOTIFICATION_TIMEOUT));
            }

            dispatch(bindPaymentOrderNo(result.transactionNumber));
            dispatch(bindDonationOrderItems({}));
            dispatch(bindConFeeOrderItem({}));
            dispatch(bindIsDonationDone(true));
            dispatch(bindShowHideForm(false));

            if (currentNavigate) {
                currentNavigate(PAGES.SUCCESS, {
                    udata,
                    totalAmount: currentAmount,
                    donateAgain: true
                });
            }
        } else {
            const errorMessage = toShortMessage(
                result.error || result.resultData,
                t('clickandpledge.PaymentFailedDescription', { error: 'Unknown gateway error' })
            );

            showTranslatedNotification(
                'error',
                'clickandpledge.PaymentFailedDescriptionTitle',
                'clickandpledge.PaymentFailedDescription',
                { error: errorMessage }
            );

            if (onSubmitRef.current || onSubmit) {
                const submitHandler = onSubmitRef.current || onSubmit;

                submitHandler(method, {
                    success: false,
                    error: errorMessage
                });
            }
        }
        isPaymentProcessingRef.current = false;
        setIsPayPalLoading(false);
        setIsAmazonPayLoading(false);
        setIsStripeLoading(false);
    }

    /**
     * handeling wether to show credit card or not
     */
    function handleShowCreditCard() {
        if (isAnyPaymentLoading) {
            return;
        }
        if (typeof setShowCreditCard === 'function') {
            setShowCreditCard(prev => !prev);
        }
    }

    /**
     * Handle payment method submission
     * Routes to appropriate payment gateway based on method
     *
     * @param {string} selectedMethod - The selected payment method
     */
    function handlePaymentSubmit(selectedMethod) {
        // Prevent concurrent payment attempts
        if (isPaymentProcessingRef.current || isAnyPaymentLoading) {
            return;
        }

        // Validate donation amount for wallet payment methods
        const walletMethods = [ 'amazonPay', 'paypalOneTime', 'paypalRecurr', 'applePay', 'gPay', 'linkPay' ];

        if (walletMethods.includes(selectedMethod) && !isDonationAmountValid()) {
            showAmountError();

            return;
        }

        // Uncheck credit card when any wallet payment method is clicked
        if (walletMethods.includes(selectedMethod) && showCreditCard) {
            setShowCreditCard(false);
        }

        if (selectedMethod === 'amazonPay') {
            handleAmazonPayClick();

            return;
        }

        if (selectedMethod === 'paypalOneTime') {
            handlePayPalOneTimeClick();

            return;
        }

        if (selectedMethod === 'paypalRecurr') {
            handlePayPalRecurringClick();

            return;
        }

        if (selectedMethod === 'applePay' || selectedMethod === 'gPay' || selectedMethod === 'linkPay') {
            handleStripePaymentClick(selectedMethod);

            return;
        }

        if (onSubmit) {
            onSubmit(selectedMethod);
        } else {
            showTranslatedNotification('error', 'clickandpledge.PaymentHandlerUnavailableTitle', 'clickandpledge.PaymentHandlerUnavailable');
        }
    }

    // Render payment method buttons
    return (
        <div className = 'payment-method-section'>

            {notification && (
                <div className = { `cnp-inline-notice ${notification.type}` }>
                    <div className = 'cnp-inline-notice-icon'>
                        {notification.type === 'error' ? '✕' : '✓'}
                    </div>
                    <div className = 'cnp-inline-notice-text'>
                        <div className = 'cnp-inline-notice-title'>{notification.title}</div>
                        <div className = 'cnp-inline-notice-message'>{notification.message}</div>
                    </div>
                </div>
            )}
            <div className = 'cnp_horizontal_divider' />
            <p className = 'cp_form_payment_method_paragraph'>{t('clickandpledge.ChoosePaymentMethod')}</p>

            <div
                className = 'cp_form_payment_method_wrapper'
                style = { paymentMethod.buttonRelative }>

                {canMakePayment?.applePay && (
                    <button
                        className = { `cp_form_payment_method_btn cp_stripe_pay_btn ${isStripeLoading ? 'loading' : ''}` }
                        disabled = { isAnyPaymentLoading }
                        id = 'cp_form_payment_method_btn_appay'
                        onClick = { () => handlePaymentSubmit('applePay') }
                        type = 'button'>
                        <img
                            alt = 'Apple Pay'
                            src = '/images/clickandpledge/applePay.svg'
                            style = { paymentMethod.logoHeight }
                            title = 'Apple Pay' />
                        <span
                            className = 'cnp-loader-dots'
                            style = { getLoaderStyle(isStripeLoading) }>
                            <div className = 'cnp-loader-dot' />
                            <div className = 'cnp-loader-dot' />
                            <div className = 'cnp-loader-dot' />
                        </span>
                    </button>
                )}

                {canMakePayment?.googlePay && (
                    <button
                        className = { `cp_form_payment_method_btn cp_stripe_pay_btn ${isStripeLoading ? 'loading' : ''}` }
                        disabled = { isAnyPaymentLoading }
                        id = 'cp_form_payment_method_btn_gpay'
                        onClick = { () => handlePaymentSubmit('gPay') }
                        type = 'button'>
                        <img
                            alt = 'Google Pay'
                            src = '/images/clickandpledge/gpay.svg'
                            style = { paymentMethod.logoHeight }
                            title = 'Google Pay' />
                        <span
                            className = 'cnp-loader-dots'
                            style = { getLoaderStyle(isStripeLoading) }>
                            <div className = 'cnp-loader-dot' />
                            <div className = 'cnp-loader-dot' />
                            <div className = 'cnp-loader-dot' />
                        </span>
                    </button>
                )}

                <button
                    className = { `cp_form_payment_method_btn cp_amazon_pay_btn ${isAmazonPayLoading ? 'loading' : ''}` }
                    disabled = { isAnyPaymentLoading }
                    id = 'cp_form_payment_method_btn_amazon'
                    onClick = { () => handlePaymentSubmit('amazonPay') }
                    type = 'button'>
                    <img
                        alt = 'Amazon Pay'
                        src = '/images/clickandpledge/amazonPay.svg'
                        style = { paymentMethod.logoHeight }
                        title = 'Amazon Pay' />
                    <span
                        className = 'cnp-loader-dots'
                        style = { getLoaderStyle(isAmazonPayLoading) }>
                        <div className = 'cnp-loader-dot' />
                        <div className = 'cnp-loader-dot' />
                        <div className = 'cnp-loader-dot' />
                    </span>
                </button>

                {recurringSelection === 'One Time Only' && (
                    <button
                        className = { `cp_form_payment_method_btn cnp-paypal-wrapper ${isPayPalLoading ? 'loading' : ''}` }
                        disabled = { isAnyPaymentLoading }
                        id = 'cp_form_payment_method_btn_pppay'
                        onClick = { () => handlePaymentSubmit('paypalOneTime') }
                        type = 'button'>
                        <img
                            alt = 'PayPal'
                            className = 'paypal-btn'
                            src = '/images/clickandpledge/payPal.svg'
                            style = { paymentMethod.logoHeight }
                            title = 'PayPal' />
                        <span
                            className = 'cnp-loader-dots'
                            style = { getLoaderStyle(isPayPalLoading) }>
                            <div className = 'cnp-loader-dot' />
                            <div className = 'cnp-loader-dot' />
                            <div className = 'cnp-loader-dot' />
                        </span>
                    </button>
                )}

                {recurringSelection !== 'One Time Only' && (<button
                    className = { `cnp_paypal_recurr cp_form_payment_method_btn cnp-paypal-wrapper ${isPayPalLoading ? 'loading' : ''}` }
                    disabled = { isAnyPaymentLoading }
                    id = 'cp_form_payment_method_btn_pppay_recurr'
                    onClick = { () => handlePaymentSubmit('paypalRecurr') }
                    type = 'button'>
                    <img
                        alt = 'PayPal'
                        className = 'paypal-btn'
                        src = '/images/clickandpledge/payPal.svg'
                        style = { paymentMethod.logoHeight }
                        title = 'PayPal' />
                    <span
                        className = 'cnp-loader-dots'
                        style = { getLoaderStyle(isPayPalLoading) }>
                        <div className = 'cnp-loader-dot' />
                        <div className = 'cnp-loader-dot' />
                        <div className = 'cnp-loader-dot' />
                    </span>
                </button>)}

                {(!canMakePayment?.googlePay && !canMakePayment?.applePay) && canMakePayment?.link && (
                    <button
                        className = { `cp_form_payment_method_btn cp_stripe_pay_btn ${isStripeLoading ? 'loading' : ''}` }
                        disabled = { isAnyPaymentLoading }
                        id = 'cp_form_payment_method_btn_lpay'
                        onClick = { () => handlePaymentSubmit('linkPay') }
                        type = 'button'>
                        <img
                            alt = 'Link'
                            src = '/images/clickandpledge/link.svg'
                            style = { paymentMethod.logoHeight }
                            title = 'Link' />
                        <span
                            className = 'cnp-loader-dots'
                            style = { getLoaderStyle(isStripeLoading) }>
                            <div className = 'cnp-loader-dot' />
                            <div className = 'cnp-loader-dot' />
                            <div className = 'cnp-loader-dot' />
                        </span>
                    </button>
                )}
            </div>

            {/* Hidden input to store Amazon Pay intent */}
            <input
                id = 'cnp_az_pay_intent'
                type = 'hidden' />

            {/* Hidden inputs to store PayPal tokens */}
            <input
                id = 'cnp_paypal_token'
                type = 'hidden' />
            <input
                id = 'cnp_paypal_ba_token'
                type = 'hidden' />


            <div className = 'cnp_horizontal_divider' />
            <p
                className = 'cp_form_payment_method_paragraph'
                style = { paymentMethod.noticeWrapper }>{t('clickandpledge.DontHaveWallet')}</p>
            <div
                className = 'cp_form_payment_method_paragraph credit_card'
                onClick = { handleShowCreditCard }
                role = 'button'
                tabIndex = { 0 }>
                <input
                    checked = { Boolean(showCreditCard) }
                    className = 'radio-styled'
                    disabled = { isAnyPaymentLoading }
                    id = 'creditCard'
                    name = 'paymentMethod'
                    onChange = { handleShowCreditCard }
                    onClick = { e => e.stopPropagation() }
                    type = 'checkbox' />
                <label
                    htmlFor = 'creditCard'
                    onClick = { e => e.stopPropagation() }
                    style = { paymentMethod.labelStyle }>{t('clickandpledge.PayWithCreditCard')}</label>
            </div>
        </div>
    );
}

export default translate(PaymentMethod);
